import type { OwnedSkin, SkinView } from '@kenocyber/shared';
import { useMemo } from 'react';

/**
 * The wardrobe: every skin COPY this account holds (gate V23.1).
 *
 * ## One card per copy, never one per skin
 *
 * Two copies of Silver Hunter are two cards, and they are not interchangeable:
 * each was born with its own immutable socket capacity of one to six (ADR
 * 0040), so the copy a player picks is a real choice and the interface has to
 * let them make it. The previous contract could not even express this - it
 * answered one entry per definition - and the whole point of contract v3 is
 * that this list is a list of instances.
 *
 * Identity is `itemInstanceId` everywhere: in the key, in the callbacks, in the
 * "which one is on" test. Never the array index, which changes the moment a
 * copy is gained, and never the `definitionId`, which two cards share.
 *
 * ## What a socket pip means, and what it must not
 *
 * An EMPTY seat. Jewels do not exist yet, nothing is installed in anything, and
 * no bonus is granted by any of this. The pips say how many seats this copy was
 * born with, which is a durable property of the copy and worth showing.
 *
 * The wording is deliberate in the other direction too. A skin must not be
 * called "purely cosmetic" or "without attributes": sockets are for jewels that
 * may grant attributes in the hunt, so an absolute denial would be a promise
 * the product has already decided not to keep.
 *
 * ## No canvas here
 *
 * A card shows the PNG the skins sidecar declares, as an `<img>`.
 * One WebGL context per tab is a rule the arena depends on, and a wardrobe that
 * spun up a renderer per card would break it for a preview.
 */

export interface SkinWardrobeProps {
  /** The catalogue: what each skin IS. Joined by `definitionId`. */
  readonly catalogue: readonly SkinView[];
  /** The shelf: every copy, duplicates included, in the server's order. */
  readonly owned: readonly OwnedSkin[];
  /**
   * Resolves a skin's PNG, or answers null.
   *
   * From the skins SIDECAR, not from the main asset manifest: a skin's picture
   * is declared in `game-assets/skin-manifest.json` beside its pivot, hull
   * centre and VFX descriptor. This screen used to ask the main manifest, which
   * has never contained a skin, so every card drew its "sem prévia" hole - and
   * in production that answer was right for a different reason, which is why
   * nothing noticed.
   */
  readonly artFor: (definitionId: string) => string | null;
  /** The copy a mutation is currently in flight for, or null. */
  readonly pendingInstanceId: string | null;
  /** Desenha os assentos de uma cópia. Ausente, o vestiário fica como estava. */
  readonly renderSockets?: (skinInstanceId: string) => React.ReactNode;
  /** True while any wardrobe mutation is in flight. Blocks every button. */
  readonly busy: boolean;
  readonly error: string | null;
  readonly onEquip: (itemInstanceId: string) => void;
  readonly onUnequip: () => void;
}

/** How a capacity is said in words, including the one that is not a number. */
export function socketCapacityLabel(capacity: number | null): string {
  /*
   * `null` is not zero, and this is the one place a reader could be told it
   * was. It means the copy predates the roulette and has no detail row, so
   * nobody knows what capacity it would have been given. Drawing zero pips
   * would state an item property that was never decided.
   */
  if (capacity === null) {
    return 'Capacidade não registrada';
  }
  return capacity === 1 ? '1 espaço disponível' : `${capacity} espaços disponíveis`;
}

/** The pips for one copy, or null when the capacity is unknown. */
function SocketPips({ capacity }: { readonly capacity: number | null }) {
  if (capacity === null) {
    return null;
  }
  /*
   * Named seats rather than raw indices.
   *
   * A pip has no identity of its own - the seats are interchangeable and
   * nothing is in them - so there is genuinely nothing to key by. Deriving a
   * stable name is the honest way to say that without asking the linter to
   * look away, and it survives the day a seat gains something to hold.
   */
  const seats = Array.from({ length: capacity }, (_, index) => `seat-${index + 1}`);
  return (
    <span className="skin-sockets" aria-hidden="true">
      {seats.map((seat) => (
        <span className="skin-socket skin-socket-empty" key={seat} />
      ))}
    </span>
  );
}

export function SkinWardrobe({
  catalogue,
  owned,
  artFor,
  pendingInstanceId,
  busy,
  error,
  onEquip,
  onUnequip,
  renderSockets,
}: SkinWardrobeProps) {
  const byDefinition = useMemo(
    () => new Map(catalogue.map((entry) => [entry.definitionId, entry])),
    [catalogue],
  );

  if (owned.length === 0) {
    return (
      <div className="skin-wardrobe">
        <p className="skin-empty">
          Você ainda não tem nenhuma skin. Elas trocam a aparência do casco da sua nave — os
          atributos continuam vindo da nave equipada.
        </p>
      </div>
    );
  }

  return (
    <div className="skin-wardrobe" data-testid="skin-wardrobe">
      <p className="skin-note">
        Cada cópia é única e nasce com sua própria quantidade de espaços. Cada espaço recebe uma
        joia que a conta já possui, e a mesma joia só ocupa um espaço de cada vez.
      </p>
      {error ? (
        <p className="skin-error" role="alert">
          {error}
        </p>
      ) : null}
      <ul className="skin-list">
        {owned.map((copy) => {
          const definition = byDefinition.get(copy.definitionId);
          // Null before the sidecar has loaded, which is a real state on a
          // cold open: the card draws its honest hole rather than a broken img.
          const art = artFor(copy.definitionId);
          const pending = pendingInstanceId === copy.itemInstanceId;
          return (
            <li
              className={copy.equipped ? 'skin-card skin-card-equipped' : 'skin-card'}
              /*
               * Identity by INSTANCE, in the DOM as well as in the callbacks.
               *
               * Two copies of Silver Hunter are two cards with the same name
               * and the same picture, so a browser test that located one by
               * text would be picking whichever came first. The capacity is
               * published beside it because it is the visible difference
               * between them and the one thing a spec can assert did not move.
               */
              data-testid={`skin-card-${copy.itemInstanceId}`}
              data-definition={copy.definitionId}
              data-capacity={copy.socketCapacity === null ? 'unknown' : String(copy.socketCapacity)}
              data-equipped={copy.equipped ? 'true' : 'false'}
              key={copy.itemInstanceId}
            >
              <div className="skin-art">
                {art ? (
                  <img alt="" src={art} />
                ) : (
                  /*
                   * An honest hole rather than a borrowed picture. The three
                   * official skins are draft and ship no art to production, so
                   * a card here legitimately has nothing to show - and showing
                   * some other skin's PNG would be worse than showing none.
                   */
                  <span className="skin-art-missing">sem prévia</span>
                )}
              </div>
              <div className="skin-body">
                <h4 className="skin-name">{definition?.displayName ?? copy.definitionId}</h4>
                {definition ? <p className="skin-blurb">{definition.blurb}</p> : null}
                <p className="skin-capacity">
                  <SocketPips capacity={copy.socketCapacity} />
                  <span className="skin-capacity-label">
                    {socketCapacityLabel(copy.socketCapacity)}
                  </span>
                </p>
                {/*
                 * Os assentos DESTA cópia, quando quem monta o vestiário os
                 * sabe desenhar (gate V28C).
                 *
                 * Uma função vinda de fora, e não um conhecimento deste
                 * componente: o vestiário sabe o que é uma cópia de skin e não
                 * tem de saber o que é uma joia. Sem ela - numa suíte que monta
                 * este componente sozinho, por exemplo - a carta continua a ser
                 * exatamente o que era.
                 */}
                {renderSockets?.(copy.itemInstanceId) ?? null}
              </div>
              <div className="skin-actions">
                {copy.equipped ? (
                  <>
                    <span className="skin-badge">Equipada</span>
                    <button
                      className="skin-button"
                      data-testid="skin-unequip"
                      disabled={busy}
                      onClick={onUnequip}
                      type="button"
                    >
                      {pending ? 'Retirando…' : 'Retirar'}
                    </button>
                  </>
                ) : (
                  <button
                    className="skin-button skin-button-primary"
                    data-testid={`skin-equip-${copy.itemInstanceId}`}
                    disabled={busy}
                    onClick={() => onEquip(copy.itemInstanceId)}
                    type="button"
                  >
                    {pending ? 'Equipando…' : 'Equipar'}
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
